/**
 * Shade-set gallery filter for Dawn swatch picker.
 *
 * Structure (single media-gallery):
 *   #Slider-Gallery-*     → main viewer (Dawn updateMedia touches THIS ul only)
 *   #Slider-Thumbnails-*  → one thumbnail list (all shade groups live here)
 *
 * Why it looked like "two galleries":
 *   All shade groups are siblings in ONE thumbnail ul. Without hide CSS, set A + set B
 *   look like a duplicated gallery.
 *
 * Why the viewer went blank on switch:
 *   Dawn setActiveMedia() marks the new featured slide is-active while it may still have
 *   --hide-variant from the previous shade. Thumbnail layouts only show .is-active, and
 *   hide CSS forces display:none → empty main image until hide classes are corrected.
 */
(function () {
  const HIDE_VIEWER = 'product__media-item--hide-variant';
  const HIDE_THUMB = 'thumbnail-list__item--hide-variant';
  const pending = new Map();
  const patchedGalleries = new WeakSet();

  function getGallery(sectionId) {
    return document.getElementById(`MediaGallery-${sectionId}`);
  }

  function isFilterEnabled(gallery) {
    return gallery?.dataset.hideVariants === 'true';
  }

  function isVisibleGroup(group, variantId) {
    if (!group || group === 'shared') return true;
    return String(group) === String(variantId);
  }

  function readSelectedVariant(sectionId) {
    const json = document
      .getElementById(`variant-selects-${sectionId}`)
      ?.querySelector('[data-selected-variant]');
    if (!json) return null;
    try {
      return JSON.parse(json.textContent);
    } catch {
      return null;
    }
  }

  function unhideGroup(gallery, groupId) {
    if (!gallery || groupId == null || groupId === '') return;
    gallery.querySelectorAll(`[data-variant-group="${CSS.escape(String(groupId))}"]`).forEach((el) => {
      el.classList.remove(HIDE_VIEWER, HIDE_THUMB);
    });
  }

  function patchSetActiveMedia(gallery) {
    if (!gallery || patchedGalleries.has(gallery)) return;
    if (typeof gallery.setActiveMedia !== 'function') return;

    const original = gallery.setActiveMedia.bind(gallery);
    gallery.setActiveMedia = (mediaId, prepend) => {
      const slide = gallery.querySelector(`.product__media-list [data-media-id="${mediaId}"]`);
      if (slide) {
        // Unhide this slide's shade group BEFORE Dawn activates it (prevents blank viewer)
        unhideGroup(gallery, slide.dataset.variantGroup);
        slide.classList.remove(HIDE_VIEWER);
      }
      const thumb = gallery.querySelector(`.thumbnail-list [data-target="${mediaId}"]`);
      if (thumb) thumb.classList.remove(HIDE_THUMB);

      return original(mediaId, prepend === true);
    };

    patchedGalleries.add(gallery);
  }

  function resetSlider(sliderComponent) {
    if (!sliderComponent || typeof sliderComponent.resetPages !== 'function') return;
    sliderComponent.resetPages();
    if (sliderComponent.currentPageElement) sliderComponent.currentPageElement.textContent = '1';
    if (sliderComponent.slider) {
      sliderComponent.slider.scrollLeft = 0;
      sliderComponent.slider.scrollTop = 0;
    }
  }

  function applyFilter(sectionId, variantId, featuredMediaId) {
    const gallery = getGallery(sectionId);
    if (!gallery || !isFilterEnabled(gallery) || !variantId) return;

    patchSetActiveMedia(gallery);

    gallery.dataset.selectedVariantId = String(variantId);
    if (featuredMediaId) gallery.dataset.featuredMediaId = String(featuredMediaId);

    const viewerItems = Array.from(
      gallery.querySelectorAll('.product__media-list > li[data-variant-group]')
    );
    const thumbItems = Array.from(
      gallery.querySelectorAll('.thumbnail-list > li[data-variant-group]')
    );

    // 1) Unhide selected shade first (never leave active slide hidden)
    unhideGroup(gallery, variantId);
    unhideGroup(gallery, 'shared');

    // 2) Hide every other shade group
    viewerItems.forEach((item) => {
      const visible = isVisibleGroup(item.dataset.variantGroup, variantId);
      item.classList.toggle(HIDE_VIEWER, !visible);
    });
    thumbItems.forEach((item) => {
      const visible = isVisibleGroup(item.dataset.variantGroup, variantId);
      item.classList.toggle(HIDE_THUMB, !visible);
    });

    let visibleCount = 0;
    let firstVisibleMediaId = null;
    viewerItems.forEach((item) => {
      if (!item.classList.contains(HIDE_VIEWER)) {
        visibleCount += 1;
        if (!firstVisibleMediaId) firstVisibleMediaId = item.dataset.mediaId;
      }
    });

    const preferredMediaId = featuredMediaId ? `${sectionId}-${featuredMediaId}` : null;
    const preferredVisible =
      preferredMediaId &&
      gallery.querySelector(
        `.product__media-list [data-media-id="${preferredMediaId}"]:not(.${HIDE_VIEWER})`
      );
    const activeMediaId = preferredVisible ? preferredMediaId : firstVisibleMediaId;
    if (!activeMediaId) return;

    // 3) Activate without prepend (Dawn already prepended in updateMedia)
    viewerItems.forEach((item) => item.classList.remove('is-active'));
    thumbItems.forEach((item) => item.querySelector('button')?.removeAttribute('aria-current'));

    const activeSlide = gallery.querySelector(
      `.product__media-list [data-media-id="${activeMediaId}"]`
    );
    if (activeSlide) {
      activeSlide.classList.remove(HIDE_VIEWER);
      activeSlide.classList.add('is-active');
    }

    const activeThumb = gallery.querySelector(`.thumbnail-list [data-target="${activeMediaId}"]`);
    if (activeThumb) {
      activeThumb.classList.remove(HIDE_THUMB);
      activeThumb.querySelector('button')?.setAttribute('aria-current', 'true');
    }

    if (typeof gallery.setActiveMedia === 'function') {
      gallery.setActiveMedia(activeMediaId, false);
    }

    requestAnimationFrame(() => {
      resetSlider(gallery.querySelector(`#GalleryViewer-${sectionId}`));
      resetSlider(gallery.querySelector(`#GalleryThumbnails-${sectionId}`));

      const thumbSlider = gallery.querySelector(`#GalleryThumbnails-${sectionId}`);
      if (thumbSlider) {
        const visibleThumbs = thumbItems.filter((item) => !item.classList.contains(HIDE_THUMB)).length;
        thumbSlider.classList.toggle('thumbnail-slider--no-slide', visibleThumbs <= 3);
      }

      const total = gallery.querySelector('.slider-counter--total');
      if (total) total.textContent = String(Math.max(visibleCount, 1));
    });
  }

  function scheduleFilter(sectionId, variantId, featuredMediaId) {
    if (!sectionId || !variantId) return;
    const key = String(sectionId);
    clearTimeout(pending.get(key));
    pending.set(
      key,
      window.setTimeout(() => {
        pending.delete(key);
        applyFilter(key, String(variantId), featuredMediaId ? String(featuredMediaId) : null);
      }, 0)
    );
  }

  function initGalleries() {
    document.querySelectorAll('media-gallery[data-hide-variants="true"]').forEach((gallery) => {
      patchSetActiveMedia(gallery);
      const sectionId = gallery.id.replace(/^MediaGallery-/, '');
      const selected = readSelectedVariant(sectionId);
      const variantId = selected?.id || gallery.dataset.selectedVariantId;
      const featuredMediaId = selected?.featured_media?.id || gallery.dataset.featuredMediaId || null;
      if (variantId) applyFilter(sectionId, variantId, featuredMediaId);
    });
  }

  window.BindtVariantMedia = {
    filter: (variantId, featuredMediaId, sectionId) =>
      scheduleFilter(sectionId, variantId, featuredMediaId),
  };

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      const sectionId = data?.sectionId;
      let variant = data?.variant;
      if (!sectionId) return;
      if (!variant?.id) variant = readSelectedVariant(sectionId);
      if (!variant?.id) return;
      scheduleFilter(sectionId, variant.id, variant.featured_media?.id ?? null);
    });
  }

  document.addEventListener('product-info:loaded', initGalleries);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGalleries);
  } else {
    initGalleries();
  }
})();